/**
 * Custom footer 3D model (sections/custom-footer.liquid)
 *
 * <custom-footer-model data-model-url data-model-scale data-auto-rotate>
 *   Renders a GLB (Draco supported) into its <canvas> with three.js r180.
 *   Drag (mouse or touch) on the model to turn it; it slowly spins on its own.
 *
 * Scene, lights, shadows and timings match the live site. three.js (~390 KiB)
 * is only fetched once the visitor has interacted with the page and the footer
 * is within 400px of the viewport. Not loaded at all on Data Saver, or on phones when
 * data-mobile="false" (theme setting "Show 3D model on phones"). "three" and "three/addons/" are mapped in
 * the import map in layout/theme.liquid. Safe to load twice.
 */

const DRACO_DECODER_PATH = 'https://www.gstatic.com/draco/versioned/decoders/1.5.6/';
const ROTATION_SPEED = 0.02;
const AUTO_ROTATE_SPEED = 0.003;
const LERP_FACTOR = 0.1;

/**
 * Runs the callback once the element is near the viewport AND the visitor has
 * scrolled, touched, clicked or pressed a key (same as the live global.js helper).
 * @param {Element} element
 * @param {() => void} callback
 * @param {AbortSignal} signal
 */
function onNearViewportAfterInteraction(element, callback, signal) {
  const events = ['scroll', 'touchstart', 'pointerdown', 'keydown'];
  let near = false;
  let interacted = false;

  const cleanUp = () => {
    observer.disconnect();
    events.forEach((event) => window.removeEventListener(event, onInteraction));
  };

  const run = () => {
    if (!near || !interacted) return;
    cleanUp();
    callback();
  };

  const onInteraction = () => {
    interacted = true;
    run();
  };

  const observer = new IntersectionObserver(
    (entries) => {
      near = entries[0].isIntersecting || entries[0].boundingClientRect.top < 0;
      run();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  events.forEach((event) => window.addEventListener(event, onInteraction, { passive: true }));
  observer.observe(element);
  signal.addEventListener('abort', cleanUp, { once: true });
}

if (!customElements.get('custom-footer-model')) {
  class CustomFooterModel extends HTMLElement {
    #controller = new AbortController();
    #frame = null;
    #renderer = null;
    #dracoLoader = null;
    #visibilityObserver = null;
    #resizeObserver = null;

    connectedCallback() {
      const canvas = this.querySelector('canvas');
      if (!canvas || !this.dataset.modelUrl) return;
      // Skip the 3D files on Data Saver, and on phones when "Show 3D model on phones" is off
      if (navigator.connection?.saveData) return;
      if (this.dataset.mobile === 'false' && window.matchMedia('(max-width: 749px)').matches) return;

      const { signal } = this.#controller;
      onNearViewportAfterInteraction(this, () => this.#init(canvas, signal), signal);
    }

    disconnectedCallback() {
      this.#controller.abort();
      this.#controller = new AbortController();
      if (this.#frame !== null) cancelAnimationFrame(this.#frame);
      this.#frame = null;
      this.#visibilityObserver?.disconnect();
      this.#resizeObserver?.disconnect();
      this.#dracoLoader?.dispose();
      this.#renderer?.dispose();
      this.#renderer = null;
    }

    /**
     * @param {HTMLCanvasElement} canvas
     * @param {AbortSignal} signal
     */
    async #init(canvas, signal) {
      const THREE = await import('three');
      const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
      const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');
      if (signal.aborted) return;

      const modelScale = parseFloat(this.dataset.modelScale) || 0.56;
      const autoRotate = this.dataset.autoRotate !== 'false' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Scene
      const scene = new THREE.Scene();

      // Camera
      const camera = new THREE.PerspectiveCamera(30, canvas.clientWidth / canvas.clientHeight, 0.1, 20);
      camera.position.set(0, 0, 8);
      camera.lookAt(0, 0, 0);

      // Renderer
      const renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
      });
      renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);
      renderer.setClearColor(0xffffff, 0);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio * 5, 2));
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.VSMShadowMap;
      this.#renderer = renderer;

      // Plane (ground)
      const planeGeometry = new THREE.PlaneGeometry(20, 20);
      const planeMaterial = new THREE.ShadowMaterial({ opacity: 0.1 });
      const plane = new THREE.Mesh(planeGeometry, planeMaterial);
      plane.rotation.x = -Math.PI / 2;
      plane.position.y = -1.7;
      plane.receiveShadow = true;
      scene.add(plane);

      // DRACO Loader
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath(DRACO_DECODER_PATH);
      this.#dracoLoader = dracoLoader;

      // GLTF Loader
      const loader = new GLTFLoader();
      loader.setDRACOLoader(dracoLoader);

      let model = null;
      const modelPivot = new THREE.Group();
      scene.add(modelPivot);

      loader.load(this.dataset.modelUrl, (gltf) => {
        if (signal.aborted) return;
        model = gltf.scene;
        model.scale.set(modelScale, modelScale, modelScale);

        // Center the model inside pivot
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        model.position.sub(center);

        model.traverse((child) => {
          if (child.isMesh) child.castShadow = true;
        });

        modelPivot.add(model);
      });

      // Lights
      const ambientLight = new THREE.AmbientLight(0xffffff, 2.8);
      scene.add(ambientLight);

      const dirLight = new THREE.DirectionalLight(0xffffff, 2.4);
      dirLight.position.set(5, 10, 5);
      dirLight.castShadow = true;
      dirLight.shadow.mapSize.width = 256;
      dirLight.shadow.mapSize.height = 256;
      dirLight.shadow.camera.near = 0.5;
      dirLight.shadow.camera.far = 25;
      dirLight.shadow.camera.left = -10;
      dirLight.shadow.camera.right = 10;
      dirLight.shadow.camera.top = 10;
      dirLight.shadow.camera.bottom = -10;
      dirLight.shadow.radius = 5;
      dirLight.shadow.blurSamples = 15;
      scene.add(dirLight);

      // Raycaster setup: dragging only starts on the model itself
      let isDragging = false;
      let previousPointerX = 0;
      const raycaster = new THREE.Raycaster();
      const pointer = new THREE.Vector2();
      let targetRotationY = 0;

      const hitsModel = (clientX, clientY) => {
        if (!model) return false;
        const rect = canvas.getBoundingClientRect();
        pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(pointer, camera);
        return raycaster.intersectObject(model, true).length > 0;
      };

      const startDrag = (clientX, clientY) => {
        if (!hitsModel(clientX, clientY)) return;
        isDragging = true;
        previousPointerX = clientX;
      };

      const drag = (clientX) => {
        if (!model || !isDragging) return;
        targetRotationY += (clientX - previousPointerX) * ROTATION_SPEED;
        previousPointerX = clientX;
      };

      const stopDrag = () => {
        isDragging = false;
      };

      // Mouse events
      canvas.addEventListener('mousedown', (event) => startDrag(event.clientX, event.clientY), { signal });
      window.addEventListener('mouseup', stopDrag, { signal });
      window.addEventListener('mouseleave', stopDrag, { signal });
      window.addEventListener('mousemove', (event) => drag(event.clientX), { signal });

      // Touch events
      canvas.addEventListener(
        'touchstart',
        (event) => startDrag(event.touches[0].clientX, event.touches[0].clientY),
        { passive: true, signal }
      );
      canvas.addEventListener('touchmove', (event) => drag(event.touches[0].clientX), { passive: true, signal });
      canvas.addEventListener('touchend', stopDrag, { signal });
      canvas.addEventListener('touchcancel', stopDrag, { signal });

      // Keep the canvas sharp when the footer changes size (rotation, editor preview)
      this.#resizeObserver = new ResizeObserver(() => {
        if (!canvas.clientWidth || !canvas.clientHeight) return;
        camera.aspect = canvas.clientWidth / canvas.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);
      });
      this.#resizeObserver.observe(canvas);

      // Animate loop, only while the canvas is on screen
      const animate = () => {
        this.#frame = requestAnimationFrame(animate);
        if (!isDragging && autoRotate) {
          targetRotationY += AUTO_ROTATE_SPEED;
        }
        modelPivot.rotation.y += (targetRotationY - modelPivot.rotation.y) * LERP_FACTOR;

        renderer.render(scene, camera);
      };

      this.#visibilityObserver = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && this.#frame === null) {
          animate();
        } else if (!entries[0].isIntersecting && this.#frame !== null) {
          cancelAnimationFrame(this.#frame);
          this.#frame = null;
        }
      });
      this.#visibilityObserver.observe(canvas);
    }
  }

  customElements.define('custom-footer-model', CustomFooterModel);
}
